import StaffProfilePanel from '../components/StaffProfilePanel';
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import { get } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const colours = {
  brown:'#3B2A21',
  green:'#556B2F',
  cream:'#F2EFE7',
  gold:'#A8893D',
  red:'#8F2E24',
  border:'#E4DED3',
  muted:'#756F68'
};

const roleLabel = value =>
  String(value || 'Not assigned')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());

const dateValue = value => {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString('en-NG', {
    day:'numeric',
    month:'long',
    year:'numeric'
  });
};

const hasValue = value => {
  if (value === true || value === false) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.some(item =>
      item &&
      Object.values(item).some(entry =>
        String(entry || '').trim()
      )
    );
  }

  return String(value || '').trim().length > 0;
};

function Value({ value, date = false }) {
  if (!hasValue(value)) {
    return (
      <span style={{
        color:'#AAA29A',
        fontStyle:'italic'
      }}>
        Not provided
      </span>
    );
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  return date ? dateValue(value) : String(value);
}

function InfoSection({ title, rows }) {
  return (
    <section style={{ marginBottom:24 }}>
      <div style={{
        color:colours.green,
        fontSize:11,
        fontWeight:700,
        letterSpacing:'.08em',
        textTransform:'uppercase',
        marginBottom:8,
        paddingBottom:6,
        borderBottom:`2px solid ${colours.cream}`
      }}>
        {title}
      </div>

      <div style={{
        border:`1px solid ${colours.border}`,
        borderRadius:8,
        overflow:'hidden'
      }}>
        {rows.map((row, index) => (
          <div
            key={row.label}
            style={{
              display:'grid',
              gridTemplateColumns:
                'minmax(150px, 34%) 1fr',
              borderBottom:
                index === rows.length - 1
                  ? 'none'
                  : `1px solid ${colours.border}`
            }}
          >
            <div style={{
              padding:'9px 12px',
              background:'#FAF9F6',
              color:colours.muted,
              fontSize:11,
              fontWeight:700,
              textTransform:'uppercase'
            }}>
              {row.label}
            </div>

            <div style={{
              padding:'9px 12px',
              color:colours.brown,
              fontSize:13,
              overflowWrap:'anywhere',
              whiteSpace:'pre-wrap'
            }}>
              <Value
                value={row.value}
                date={row.date}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function HistorySection({
  title,
  records,
  fields,
  emptyText
}) {
  const meaningful = Array.isArray(records)
    ? records.filter(record =>
        record &&
        Object.values(record).some(value =>
          String(value || '').trim()
        )
      )
    : [];

  return (
    <section style={{ marginBottom:24 }}>
      <div style={{
        color:colours.green,
        fontSize:11,
        fontWeight:700,
        letterSpacing:'.08em',
        textTransform:'uppercase',
        marginBottom:8,
        paddingBottom:6,
        borderBottom:`2px solid ${colours.cream}`
      }}>
        {title}
      </div>

      {meaningful.length === 0 ? (
        <div style={{
          border:`1px solid ${colours.border}`,
          borderRadius:8,
          padding:14,
          color:'#AAA29A',
          fontSize:13,
          fontStyle:'italic'
        }}>
          {emptyText || 'Not provided'}
        </div>
      ) : (
        <div style={{
          display:'flex',
          flexDirection:'column',
          gap:10
        }}>
          {meaningful.map((record, index) => (
            <div
              key={`${title}-${index}`}
              style={{
                border:`1px solid ${colours.border}`,
                borderRadius:8,
                overflow:'hidden'
              }}
            >
              <div style={{
                background:colours.cream,
                color:colours.brown,
                padding:'7px 12px',
                fontWeight:700,
                fontSize:12
              }}>
                {title} {index + 1}
              </div>

              {fields.map((field, fieldIndex) => (
                <div
                  key={field.key}
                  style={{
                    display:'grid',
                    gridTemplateColumns:
                      'minmax(150px, 34%) 1fr',
                    borderBottom:
                      fieldIndex === fields.length - 1
                        ? 'none'
                        : `1px solid ${colours.border}`
                  }}
                >
                  <div style={{
                    padding:'8px 12px',
                    color:colours.muted,
                    background:'#FAF9F6',
                    fontSize:11,
                    fontWeight:700,
                    textTransform:'uppercase'
                  }}>
                    {field.label}
                  </div>

                  <div style={{
                    padding:'8px 12px',
                    color:colours.brown,
                    fontSize:13,
                    whiteSpace:'pre-wrap'
                  }}>
                    <Value
                      value={record[field.key]}
                      date={field.date}
                    />
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function BiodataModal({ staff, onClose }) {
  const [profileTab, setProfileTab] = useState("overview");
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    setLoading(true);
    setError('');

    get(`/biodata/staff-records/${staff.id}`)
      .then(response => {
        if (active) {
          setRecord(response?.data || null);
        }
      })
      .catch(err => {
        if (active) {
          setError(
            err.message ||
            'Staff biodata could not be loaded'
          );
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [staff.id]);

  const form = record?.form_data || {};

  return (
    <div
      className="modal-overlay"
      onClick={event => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="modal" style={{
        width:'96vw',
        maxWidth:980,
        maxHeight:'94vh',
        display:'flex',
        flexDirection:'column'
      }}>
        <div className="modal-hdr" style={{
          background:colours.brown,
          borderRadius:'12px 12px 0 0'
        }}>
          <div>
            <div style={{
              color:colours.gold,
              fontWeight:700,
              fontSize:17
            }}>
              {staff.name}
            </div>

            <div style={{
              color:'rgba(242,239,231,.68)',
              fontSize:11,
              marginTop:3
            }}>
              {roleLabel(staff.role)}
              {staff.staff_id
                ? ` · ${staff.staff_id}`
                : ''}
            </div>
          </div>

          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            style={{ color:colours.cream }}
          >
            ✕
          </button>
        </div>

        <div className="modal-body" style={{
          overflowY:'auto',
          padding:20
        }}>
          <StaffProfilePanel staffId={staff.id} tab={profileTab} onTabChange={setProfileTab} />
          <div hidden={profileTab !== "personal"}>
          {loading ? (
            <div className="spinner" />
          ) : error ? (
            <div className="alert alert-error">
              {error}
            </div>
          ) : !record?.submission_id ? (
            <div style={{
              padding:30,
              textAlign:'center',
              color:colours.muted
            }}>
              <div style={{
                fontSize:36,
                marginBottom:10
              }}>
                📋
              </div>
              This staff member has not submitted a biodata form.
            </div>
          ) : (
            <>
              <div style={{
                display:'flex',
                justifyContent:'space-between',
                gap:12,
                flexWrap:'wrap',
                padding:12,
                marginBottom:20,
                borderRadius:8,
                background:'#E8F3E8',
                color:'#2E6B3A',
                fontSize:12
              }}>
                <span>
                  Submitted: <strong>
                    {dateValue(record.submitted_at)}
                  </strong>
                </span>

                <span>
                  Review: <strong>
                    {roleLabel(
                      record.review_status || 'pending'
                    )}
                  </strong>
                </span>
              </div>

              <InfoSection
                title="Staff Record"
                rows={[
                  {
                    label:'Staff Name',
                    value:record.name
                  },
                  {
                    label:'Staff ID',
                    value:record.staff_id
                  },
                  {
                    label:'Role',
                    value:roleLabel(record.role)
                  },
                  {
                    label:'Employment Status',
                    value:roleLabel(
                      record.employment_status
                    )
                  },
                  {
                    label:'Date Joined',
                    value:record.date_joined,
                    date:true
                  }
                ]}
              />

              <InfoSection
                title="Personal Information"
                rows={[
                  {
                    label:'Title',
                    value:form.title
                  },
                  {
                    label:'Full Name',
                    value:record.name
                  },
                  {
                    label:'Maiden Name',
                    value:form.maiden_name
                  },
                  {
                    label:'Marital Status',
                    value:form.marital_status
                  },
                  {
                    label:'Date of Birth',
                    value:form.date_of_birth,
                    date:true
                  },
                  {
                    label:'Place of Birth',
                    value:form.place_of_birth
                  },
                  {
                    label:'Nationality',
                    value:form.nationality
                  },
                  {
                    label:'Sex',
                    value:form.sex
                  },
                  {
                    label:'State of Origin',
                    value:form.state_of_origin
                  },
                  {
                    label:'Local Government Area',
                    value:form.lga
                  },
                  {
                    label:'Phone Number',
                    value:form.phone
                  },
                  {
                    label:'Email Address',
                    value:form.email || record.email
                  },
                  {
                    label:'Permanent Address',
                    value:form.permanent_address
                  },
                  {
                    label:'Residential Address',
                    value:form.residential_address
                  }
                ]}
              />

              <InfoSection
                title="Health Information"
                rows={[
                  {
                    label:'Genotype',
                    value:form.genotype
                  },
                  {
                    label:'Blood Group',
                    value:form.blood_group
                  },
                  {
                    label:'Health History',
                    value:form.health_history
                  }
                ]}
              />

              <HistorySection
                title="Education"
                records={form.educational_history}
                emptyText="No educational history provided"
                fields={[
                  {
                    key:'degree',
                    label:'Degree or Qualification'
                  },
                  {
                    key:'institution',
                    label:'Institution'
                  },
                  {
                    key:'year',
                    label:'Year'
                  }
                ]}
              />

              <HistorySection
                title="Professional Qualification"
                records={
                  form.professional_qualifications
                }
                emptyText=
                  "No professional qualifications provided"
                fields={[
                  {
                    key:'body',
                    label:'Professional Body'
                  },
                  {
                    key:'grade',
                    label:'Membership Grade'
                  },
                  {
                    key:'year',
                    label:'Year'
                  }
                ]}
              />

              <HistorySection
                title="Employment"
                records={form.employment_history}
                emptyText="No employment history provided"
                fields={[
                  {
                    key:'name',
                    label:'Organisation'
                  },
                  {
                    key:'position',
                    label:'Position'
                  },
                  {
                    key:'address',
                    label:'Address'
                  },
                  {
                    key:'phone',
                    label:'Telephone'
                  },
                  {
                    key:'date_from',
                    label:'Date From',
                    date:true
                  },
                  {
                    key:'date_to',
                    label:'Date To',
                    date:true
                  },
                  {
                    key:'description',
                    label:'Responsibilities'
                  }
                ]}
              />

              <InfoSection
                title="Spouse Information"
                rows={[
                  {
                    label:'Name',
                    value:form.spouse_name
                  },
                  {
                    label:'Maiden Name',
                    value:form.spouse_maiden_name
                  },
                  {
                    label:'Email',
                    value:form.spouse_email
                  },
                  {
                    label:'Phone',
                    value:form.spouse_phone
                  },
                  {
                    label:'Permanent Address',
                    value:form.spouse_address
                  },
                  {
                    label:'Contact Address',
                    value:form.spouse_contact_address
                  }
                ]}
              />

              <HistorySection
                title="Child"
                records={form.children}
                emptyText="No children provided"
                fields={[
                  {
                    key:'name',
                    label:'Name'
                  },
                  {
                    key:'sex',
                    label:'Sex'
                  },
                  {
                    key:'dob',
                    label:'Date of Birth',
                    date:true
                  }
                ]}
              />

              <InfoSection
                title="Parents"
                rows={[
                  {
                    label:"Father's Name",
                    value:form.father_name
                  },
                  {
                    label:"Father's Phone",
                    value:form.father_phone
                  },
                  {
                    label:"Mother's Name",
                    value:form.mother_name
                  },
                  {
                    label:"Mother's Phone",
                    value:form.mother_phone
                  }
                ]}
              />

              <InfoSection
                title="Emergency Contact"
                rows={[
                  {
                    label:'Name',
                    value:form.emergency_contact_name
                  },
                  {
                    label:'Sex',
                    value:form.emergency_contact_sex
                  },
                  {
                    label:'Relationship',
                    value:
                      form.emergency_contact_relationship
                  },
                  {
                    label:'Phone',
                    value:form.emergency_contact_phone
                  },
                  {
                    label:'Email',
                    value:form.emergency_contact_email
                  }
                ]}
              />

              <InfoSection
                title="Criminal Declaration"
                rows={[
                  {
                    label:'Criminal Question 1',
                    value:form.criminal_q1
                  },
                  {
                    label:'Criminal Question 2',
                    value:form.criminal_q2
                  },
                  {
                    label:'Declaration Details',
                    value:form.criminal_details
                  }
                ]}
              />

              <InfoSection
                title="Certification"
                rows={[
                  {
                    label:'Information Certified',
                    value:form.certified
                  },
                  {
                    label:'Submission Date',
                    value:record.submitted_at,
                    date:true
                  },
                  {
                    label:'Review Status',
                    value:roleLabel(
                      record.review_status || 'pending'
                    )
                  },
                  {
                    label:'Review Note',
                    value:record.review_note
                  },
                  {
                    label:'Reviewed Date',
                    value:record.reviewed_at,
                    date:true
                  }
                ]}
              />
            </>
          )}
          </div>
        </div>

        <div className="modal-ftr">
          <button
            type="button"
            className="btn btn-outline"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StaffBiodata() {
  const { user } = useAuth();
  const allowed = [
    'super_admin',
    'hr_officer'
  ].includes(user?.role);

  const [records, setRecords] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!allowed) return;

    setLoading(true);
    setError('');

    try {
      const response = await get(
        '/biodata/staff-records'
      );

      setRecords(response?.data || []);
    } catch (err) {
      setError(
        err.message ||
        'Staff biodata directory could not be loaded'
      );
    } finally {
      setLoading(false);
    }
  }, [allowed]);

  useEffect(() => {
    load();
  }, [load]);

  if (!allowed) {
    return (
      <div className="page">
        <div className="alert alert-error">
          Only the Managing Director and HR Officer can
          access staff biodata.
        </div>
      </div>
    );
  }

  const normalizedSearch =
    search.trim().toLowerCase();

  const filtered = records.filter(record => {
    const matchesSearch =
      !normalizedSearch ||
      [
        record.name,
        record.email,
        record.staff_id,
        record.role
      ].some(value =>
        String(value || '')
          .toLowerCase()
          .includes(normalizedSearch)
      );

    const matchesFilter =
      filter === 'all' ||
      (
        filter === 'submitted' &&
        record.biodata_status === 'submitted'
      ) ||
      (
        filter === 'missing' &&
        record.biodata_status !== 'submitted'
      );

    return matchesSearch && matchesFilter;
  });

  const submittedCount = records.filter(
    record => record.biodata_status === 'submitted'
  ).length;

  return (
    <div className="page">
      <div style={{
        display:'flex',
        justifyContent:'space-between',
        alignItems:'flex-start',
        gap:14,
        flexWrap:'wrap',
        marginBottom:18
      }}>
        <div>
          <h1 style={{
            margin:0,
            color:colours.brown,
            fontSize:26
          }}>
            Staff Profiles
          </h1>

          <p style={{
            margin:'5px 0 0',
            color:colours.muted,
            fontSize:12
          }}>
            Employment, attendance, work and career records
          </p>
        </div>

        <button
          type="button"
          className="btn btn-outline"
          onClick={load}
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh'}
        </button>
        <a className="btn btn-primary" href="/non-production-assessments">Staff Assessments</a>
      </div>

      <div style={{
        display:'grid',
        gridTemplateColumns:
          'repeat(auto-fit, minmax(180px, 1fr))',
        gap:12,
        marginBottom:18
      }}>
        {[
          ['Total Staff', records.length, colours.brown],
          ['Submitted', submittedCount, colours.green],
          [
            'Not Submitted',
            records.length - submittedCount,
            colours.gold
          ]
        ].map(([label, value, colour]) => (
          <div
            key={label}
            className="card"
            style={{
              padding:16,
              borderTop:`4px solid ${colour}`
            }}
          >
            <div style={{
              color:colours.muted,
              fontSize:10,
              fontWeight:700,
              textTransform:'uppercase'
            }}>
              {label}
            </div>

            <div style={{
              color:colours.brown,
              fontSize:26,
              fontWeight:700,
              marginTop:5
            }}>
              {value}
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        <div style={{
          display:'flex',
          gap:10,
          flexWrap:'wrap',
          padding:14,
          borderBottom:`1px solid ${colours.border}`
        }}>
          <input
            className="form-control"
            value={search}
            onChange={event =>
              setSearch(event.target.value)
            }
            placeholder=
              "Search name, email, staff ID or role"
            style={{
              flex:'1 1 280px',
              maxWidth:440
            }}
          />

          <select
            className="form-control"
            value={filter}
            onChange={event =>
              setFilter(event.target.value)
            }
            style={{
              width:190,
              flex:'0 0 190px'
            }}
          >
            <option value="all">All Staff</option>
            <option value="submitted">Submitted</option>
            <option value="missing">Not Submitted</option>
          </select>
        </div>

        {error && (
          <div className="alert alert-error" style={{
            margin:14
          }}>
            {error}
          </div>
        )}

        <div style={{ overflowX:'auto' }}>
          {loading ? (
            <div style={{ padding:30 }}>
              <div className="spinner" />
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Staff</th>
                  <th>Role</th>
                  <th>Employment</th>
                  <th>Biodata</th>
                  <th>Submitted</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="6">
                      No matching staff records.
                    </td>
                  </tr>
                ) : filtered.map(record => (
                  <tr key={record.id}>
                    <td>
                      <div style={{ fontWeight:600 }}>
                        {record.name}
                      </div>
                      <div style={{
                        color:colours.muted,
                        fontSize:11,
                        marginTop:2
                      }}>
                        {record.email}
                        {record.staff_id
                          ? ` · ${record.staff_id}`
                          : ''}
                      </div>
                    </td>

                    <td>{roleLabel(record.role)}</td>

                    <td>
                      {roleLabel(
                        record.employment_status ||
                        (
                          record.is_active
                            ? 'active'
                            : 'inactive'
                        )
                      )}
                    </td>

                    <td>
                      <span style={{
                        display:'inline-block',
                        borderRadius:20,
                        padding:'4px 9px',
                        fontSize:10,
                        fontWeight:700,
                        background:
                          record.biodata_status ===
                            'submitted'
                            ? '#E8F3E8'
                            : '#FFF4D8',
                        color:
                          record.biodata_status ===
                            'submitted'
                            ? '#2E6B3A'
                            : '#8B5E0A'
                      }}>
                        {record.biodata_status ===
                          'submitted'
                          ? 'Submitted'
                          : 'Not submitted'}
                      </span>
                    </td>

                    <td>
                      {record.submitted_at
                        ? dateValue(record.submitted_at)
                        : 'Not submitted'}
                    </td>

                    <td>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => setSelected(record)}
                      >
                        View Profile
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selected && (
        <BiodataModal
          staff={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
